import { frameThumbnail } from '@/util/thumbnailFraming';
import type { ImageOption } from '@rock/types';
import type { CSSProperties, FC } from 'react';

/** Everything the framing math and the `<img>` need — satisfied by EquipmentImage and ImageOption alike. */
export type ThumbnailImage = Pick<ImageOption, 'url' | 'thumbnail' | 'width' | 'height'> &
    Partial<Pick<ImageOption, 'rotation' | 'highlights'>>;

/** Default tile edge, in px — the size the equipment gallery has always used. */
export const THUMBNAIL_SIZE_PX = 120;

/**
 * One square image tile, painted in the orientation the user chose in the app.
 *
 * Letting each photo keep its natural aspect makes a row wildly uneven (a portrait phone shot comes
 * out a 74px sliver beside a 256px name-plate), so every tile is the same size and the image is
 * cropped to fill it — centered on the model/serial highlights when the scan flow recorded any.
 *
 * Pointing at the CARD eases every tile in it back down to whole at once, which is the escape hatch
 * for whatever the crops hid: an equipment card's photos are one subject shot several ways, so
 * revealing them together is what lets you compare them. That's why the trigger is a `group/card`
 * hover on the containing card rather than per-tile state — CSS-only, so N tiles cost N transitions
 * and zero re-renders, and a card with no `group/card` ancestor simply stays cropped.
 *
 * Both states share one `<img>` box and differ only in transform, so the browser tweens between them
 * on the compositor rather than reflowing.
 *
 * Renders a `<button>` only when given an `onClick`: it's also used inside larger clickable cards,
 * and a nested button there would be invalid markup.
 */
export const ImageThumbnail: FC<{
    image: ThumbnailImage;
    alt: string;
    size?: number;
    onClick?: () => void;
}> = ({ image, alt, size = THUMBNAIL_SIZE_PX, onClick }) => {
    const framing = frameThumbnail(image, size);

    const rotation = `rotate(${framing.rotationDeg}deg)`;
    const cover = `translate(-50%, -50%) translate(${framing.offsetX}px, ${framing.offsetY}px) ${rotation} scale(${framing.coverScale})`;
    const whole = `translate(-50%, -50%) ${rotation}`;

    const className = `relative flex-shrink-0 overflow-hidden rounded-md bg-muted${onClick ? ' cursor-pointer' : ''}`;
    const style = { width: size, height: size };

    const picture = (
        <img
            src={image.thumbnail || image.url}
            alt={alt}
            // Both transforms are class-applied, never inline: an inline `transform` would outrank
            // the hover variant and the tile would never open.
            className="absolute top-1/2 left-1/2 max-w-none object-cover transition-transform duration-200 ease-out [transform:var(--thumbnail-cover)] group-hover/card:[transform:var(--thumbnail-whole)] group-focus-within/card:[transform:var(--thumbnail-whole)]"
            style={
                {
                    width: framing.width,
                    height: framing.height,
                    '--thumbnail-cover': cover,
                    '--thumbnail-whole': whole,
                } as CSSProperties
            }
        />
    );

    return onClick ? (
        <button type="button" onClick={onClick} className={className} style={style}>
            {picture}
        </button>
    ) : (
        <div className={className} style={style}>
            {picture}
        </div>
    );
};
